import React, { useCallback, useState, useEffect } from 'react'
import { I18nextProvider } from 'react-i18next'
import i18n, { initI18n } from '@/i18n/config'
import { LoadingSpinner } from '@/components/common/ui/loading-spinner'
import { LanguageContext } from '@/contexts/language-context'

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(i18n.isInitialized)
  const [error, setError] = useState<Error | null>(null)
  const [currentLanguage, setCurrentLanguage] = useState(
    i18n.language || localStorage.getItem('i18nextLng') || 'ja'
  )

  // Ensure i18n is fully initialized
  useEffect(() => {
    let mounted = true

    // If i18n is already initialized (from the synchronous init in config.ts),
    // we don't need to initialize it again
    if (i18n.isInitialized) {
      if (mounted) {
        setIsInitialized(true)
      }
      return () => {
        mounted = false
      }
    }

    // Otherwise, initialize it asynchronously
    const initialize = async () => {
      try {
        setIsLoading(true)
        await initI18n()

        if (mounted) {
          const detectedLang = i18n.language || localStorage.getItem('i18nextLng') || 'ja'
          setCurrentLanguage(detectedLang)
          setIsInitialized(true)
        }
      } catch (err) {
        console.error('Failed to initialize i18n:', err)
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize i18n'))
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    initialize()
    return () => {
      mounted = false
    }
  }, [])

  const changeLanguage = useCallback(async (lang: 'ja' | 'en') => {
    try {
      setIsLoading(true)
      await i18n.changeLanguage(lang)
      setCurrentLanguage(lang)
      localStorage.setItem('i18nextLng', lang)

      // Force re-render of all translated components
      const event = new Event('languageChanged')
      window.dispatchEvent(event)
    } catch (err) {
      console.error('Failed to change language:', err)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Listen for language changes from other parts of the app
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLang = i18n.language || localStorage.getItem('i18nextLng') || 'ja'
      setCurrentLanguage(newLang)
    }

    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [])

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Failed to load translations: {error.message}</div>
      </div>
    )
  }

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    )
  }

  const value = {
    currentLanguage,
    changeLanguage,
    isLoading,
  }

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
    </I18nextProvider>
  )
}
